<script setup lang="ts" generic="T extends { id: number | string }">
  import type { Component } from 'vue';
  import { DefaultTable, TableHeader, HeaderTableWithButtons } from '@/shared/ui';
  import {
    useTableEvents,
    useTableState,
    type TableEmitter,
    type OrderOptions,
  } from '@/shared/lib';

  interface Column {
    text: string;
    class?: string;
  }

  const props = withDefaults(
    defineProps<{
      title: string;
      columns: Column[];
      data: T[];
      error?: string | null;
      page: number;
      orderOptions?: OrderOptions[];
      filterModal: Component;
      errorMessage?: string;
      emptyMessage?: string;
      colspan?: number;
    }>(),
    {
      error: null,
      errorMessage: 'Erro ao carregar dados',
      emptyMessage: 'Nenhum item para mostrar',
      colspan: 8,
    }
  );

  const emit = defineEmits<{
    (e: 'update:sort', sortCamp: string, orderFlow: string): void;
    (e: 'update:filter', filters: Record<string, unknown>): void;
  }>();

  const { handleSortOptions, handleFilter } = useTableEvents(emit as TableEmitter);

  const { shouldShowError, shouldShowEmpty } = useTableState<T>({
    error: () => props.error,
    data: () => props.data,
    errorTitle: 'Erro',
    errorMessage: props.errorMessage,
  });
</script>

<template>
  <DefaultTable class="divide-y divide-gray-02">
    <thead>
      <tr>
        <HeaderTableWithButtons
          id="header-table-with-buttons"
          :colspan="colspan"
          :text="title"
          class="text-gray-07 text-sm/5"
          :page="page"
          :order-options="orderOptions"
          :filter-modal="filterModal"
          @update:sort="handleSortOptions"
          @update:filter="handleFilter"
        />
      </tr>
      <tr>
        <TableHeader
          v-for="(column, index) in columns"
          :key="index"
          :text="column.text"
          :class="column.class || 'px-6 py-4.5'"
        />
      </tr>
    </thead>
    <tbody class="bg-white-01 divide-y divide-gray-02">
      <tr v-if="shouldShowError">
        <td
          :colspan="columns.length"
          class="text-center py-4 text-gray-07"
        >
          {{ errorMessage }}
        </td>
      </tr>
      <tr v-else-if="shouldShowEmpty">
        <td
          :colspan="columns.length"
          class="text-center py-4 text-gray-07"
        >
          {{ emptyMessage }}
        </td>
      </tr>
      <slot
        name="row"
        :items="data"
      />
    </tbody>
  </DefaultTable>
</template>
